// 테스트 관련 코드, 추후 삭제 예정
/**
 * 상담 채팅 테스트(고객 쪽) — 핸드오프(챗봇→상담사 연동)가 아직 없어, 이 화면에서 첫 메시지를 입력하는
 * 행위 자체가 핸드오프를 흉내낸다(case 생성 API 호출). 그 이후 조회·발송·실시간 수신은 신규 백엔드 없이
 * sv-support의 실제 엔드포인트를 그대로 호출한다. 신원 확인은 FE가 아무 헤더도 보내지 않고 FlowGate가
 * JWT에서 검증해 넣어주는 X-User-Id만 쓴다(C-05 §3.5). 목업 데이터는 전혀 쓰지 않는다.
 *
 * 흐름: 첫 메시지 전송 → case 생성(WAITING) → 상담사가 배정+대화시작하면 실시간으로 IN_PROGRESS 전환
 * → 그때부터 실제 채팅 가능. 배정 전에는 상담사 쪽 idpAdmin에서 이 case를 배정·시작해줘야 한다.
 */
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Avatar, Button, Page, PageHeader, Tag, Textarea } from '@indipia/ui-core';
import { useAuthStore } from '@indipia/shared';
import { createSupportCase, getMyActiveCase, listSupportMessages, type SupportCaseCreateDto, type SupportMessageDto } from '../api/supportChat';
import {
  connectSupportChat,
  disconnectSupportChat,
  sendSupportChatMessage,
  subscribeSupportChatTopic,
  type ConnectionState,
} from '../realtime/supportChatRealtime';

type CaseStatus = SupportCaseCreateDto['caseStatus'];

interface RealtimeEvent {
  type: string;
  [key: string]: unknown;
}

function safeParse<T>(body: string): T | null {
  try {
    return JSON.parse(body) as T;
  } catch {
    return null;
  }
}

const STATUS_META: Record<CaseStatus, { label: string; bg: string; text: string }> = {
  WAITING: { label: '대기 중', bg: 'var(--color-warningSubtle)', text: 'var(--color-warningStrong)' },
  ASSIGNED: { label: '상담사 배정됨', bg: 'var(--color-infoSubtle)', text: 'var(--color-infoStrong)' },
  IN_PROGRESS: { label: '상담 중', bg: 'var(--color-successSubtle)', text: 'var(--color-successStrong)' },
  DONE: { label: '상담 종료', bg: 'var(--shell-bg)', text: 'var(--shell-on-surface-2)' },
};

const CONNECTION_LABEL: Record<ConnectionState, string> = {
  connecting: '실시간 연결 중…',
  connected: '실시간 연결됨',
  disconnected: '연결 끊김 — 재연결 중',
};

export function TestSupportChatPage() {
  const memberId = useAuthStore((s) => s.user?.memberId);
  const memberName = useAuthStore((s) => s.user?.memberName) ?? '테스트회원';
  const [caseId, setCaseId] = useState<string | null>(null);
  const [caseStatus, setCaseStatus] = useState<CaseStatus | null>(null);
  const [connState, setConnState] = useState<ConnectionState>('connecting');
  const [messages, setMessages] = useState<SupportMessageDto[]>([]);
  const [firstRequest, setFirstRequest] = useState('');
  const [draft, setDraft] = useState('');
  const [creating, setCreating] = useState(false);
  const [checkingActive, setCheckingActive] = useState(true);
  const threadRef = useRef<HTMLDivElement>(null);

  // 화면을 벗어나면 연결을 완전히 정리한다(테스트 화면 전용 — 로그인 전역 상시 연결 아님).
  useEffect(() => disconnectSupportChat, []);

  // 이어보기 — 진입 시 이 회원의 진행중 case가 있으면 곧바로 이어서 보여준다("상담 시작" 화면 건너뜀).
  useEffect(() => {
    if (memberId == null) return;
    getMyActiveCase()
      .then((res) => {
        const active = res.data.data;
        if (active) {
          setCaseId(String(active.caseId));
          setCaseStatus(active.caseStatus);
        }
      })
      .finally(() => setCheckingActive(false));
  }, [memberId]);

  useEffect(() => {
    if (!caseId || memberId == null) return;

    connectSupportChat(setConnState);

    listSupportMessages(caseId)
      .then((res) => setMessages(res.data.data.items))
      .catch((e) => toast.error(`대화 이력을 불러오지 못했습니다 (${e?.response?.status ?? 'ERR'})`));

    return subscribeSupportChatTopic(`/topic/support.case.${caseId}`, (body) => {
      const event = safeParse<RealtimeEvent>(body);
      if (!event) return;
      if (event.type === 'MESSAGE') {
        const incoming: SupportMessageDto = {
          messageId: Number(event.messageId),
          senderType: event.senderType as SupportMessageDto['senderType'],
          senderId: event.senderId == null ? null : Number(event.senderId),
          messageContent: String(event.content),
          createdAt: String(event.createdAt),
        };
        setMessages((prev) => (prev.some((m) => m.messageId === incoming.messageId) ? prev : [...prev, incoming]));
      } else if (event.type === 'STATUS_CHANGED') {
        setCaseStatus(event.status as CaseStatus);
      }
    });
  }, [caseId, memberId]);

  useEffect(() => {
    threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  // 핸드오프 시뮬레이션(case 생성). 입력한 내용은 botContext로 저장되고, 접수 완료 후 이 문구를
  // 화면에도 고객 말풍선처럼 보여준다(실제 채팅 메시지로 저장되는 건 아님 — IN_PROGRESS 전환 후부터만
  // 채팅 메시지가 저장·전파된다).
  const handleStart = async () => {
    const text = draft.trim();
    if (!text || memberId == null || creating) return;
    setCreating(true);
    try {
      const res = await createSupportCase(memberId, memberName, text);
      setFirstRequest(text);
      setCaseId(String(res.data.data.caseId));
      setCaseStatus(res.data.data.caseStatus);
      setDraft('');
    } catch {
      toast.error('상담 요청 접수에 실패했습니다');
    } finally {
      setCreating(false);
    }
  };

  const handleSend = () => {
    const text = draft.trim();
    if (!text || !caseId) return;
    if (!sendSupportChatMessage(caseId, text)) {
      toast.error('실시간 연결이 아직 준비되지 않았습니다. 잠시 후 다시 시도해주세요');
      return;
    }
    setDraft('');
  };

  const canChat = caseStatus === 'IN_PROGRESS';
  const isDone = caseStatus === 'DONE';
  const statusMeta = STATUS_META[caseStatus ?? 'WAITING'];

  return (
    <Page layout="fixed" pad={false} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        title="상담 채팅"
        subtitle={caseId ? CONNECTION_LABEL[connState] : '고객 쪽 테스트 — 메시지를 보내면 핸드오프가 시뮬레이션됩니다'}
        style={{ padding: '16px 20px', borderBottom: '1px solid var(--shell-border)' }}
        extra={caseId && <Tag style={{ background: statusMeta.bg, color: statusMeta.text, border: 'none' }}>case #{caseId} · {statusMeta.label}</Tag>}
      />

      {checkingActive ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--shell-on-surface-2)', fontSize: 13 }}>
          진행중인 상담을 확인하는 중…
        </div>
      ) : !caseId ? (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ width: '100%', maxWidth: 420, background: 'var(--shell-surface)', border: '1px solid var(--shell-border)', borderRadius: 16, padding: 24, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>무엇을 도와드릴까요?</div>
            <div style={{ fontSize: 13, color: 'var(--shell-hint)' }}>입력하신 내용은 상담사에게 바로 접수됩니다.</div>
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="상담이 필요한 내용을 입력해주세요"
              rows={4}
            />
            <Button variant="primary" onClick={() => void handleStart()} disabled={creating || !draft.trim()}>
              {creating ? '접수 중…' : '상담 요청 보내기'}
            </Button>
          </div>
        </div>
      ) : (
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', background: 'var(--shell-bg)' }}>
          <div ref={threadRef} style={{ flex: 1, overflowY: 'auto', padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* 접수한 요청 내용 — 실제 채팅 메시지는 아니지만(botContext), 대화 흐름상 첫 말풍선처럼 보여준다.
                이어보기로 들어온 경우(방금 접수한 게 아님)는 이 화면이 원문을 모르므로 표시하지 않는다. */}
            {firstRequest && (
              <div style={{ alignSelf: 'flex-end', maxWidth: '78%' }}>
                <div style={{ background: 'var(--brand-kakao-container)', color: 'var(--brand-kakao-label)', borderRadius: 16, borderTopRightRadius: 4, padding: '10px 14px', fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                  {firstRequest}
                </div>
              </div>
            )}

            {!canChat && !isDone && (
              <div style={{ alignSelf: 'center', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--shell-hint)', background: 'var(--shell-surface)', borderRadius: 20, padding: '6px 14px' }}>
                <span className="pulse-dot" style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--color-warning)', display: 'inline-block' }} />
                {statusMeta.label} — 상담사를 연결하고 있어요
              </div>
            )}

            {messages.map((m) => {
              const isMine = m.senderType === 'CUSTOMER';
              const isSystem = m.senderType === 'SYSTEM';
              if (isSystem) {
                return (
                  <div key={m.messageId} style={{ alignSelf: 'center', fontSize: 12, color: 'var(--shell-hint)', background: 'var(--shell-surface)', borderRadius: 20, padding: '6px 14px' }}>
                    {m.messageContent}
                  </div>
                );
              }
              return (
                <div key={m.messageId} style={{ display: 'flex', gap: 8, maxWidth: '78%', alignSelf: isMine ? 'flex-end' : 'flex-start' }}>
                  {!isMine && (
                    <Avatar style={{ width: 30, height: 30, flexShrink: 0, background: 'var(--indipia-purple-600)', color: 'var(--gray-0)', fontSize: 12, fontWeight: 600 }}>
                      상담
                    </Avatar>
                  )}
                  <div>
                    {!isMine && <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-hint)', marginBottom: 3 }}>상담사</div>}
                    <div
                      style={{
                        background: isMine ? 'var(--brand-kakao-container)' : 'var(--shell-surface)',
                        color: isMine ? 'var(--brand-kakao-label)' : 'var(--shell-on-surface)',
                        borderRadius: 16,
                        borderTopRightRadius: isMine ? 4 : 16,
                        borderTopLeftRadius: isMine ? 16 : 4,
                        padding: '10px 14px',
                        fontSize: 14,
                        lineHeight: 1.5,
                        whiteSpace: 'pre-wrap',
                        boxShadow: isMine ? 'none' : '0 1px 2px var(--alpha-black-50)',
                      }}
                    >
                      {m.messageContent}
                    </div>
                  </div>
                </div>
              );
            })}

            {isDone && (
              <div style={{ alignSelf: 'center', fontSize: 12, color: 'var(--shell-hint)', background: 'var(--shell-surface)', borderRadius: 20, padding: '6px 14px' }}>
                상담이 종료되었습니다
              </div>
            )}
          </div>

          {!isDone && (
            <div style={{ display: 'flex', gap: 8, padding: '12px 16px', borderTop: '1px solid var(--shell-border)', background: 'var(--shell-surface)', alignItems: 'flex-end' }}>
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                disabled={!canChat}
                placeholder={canChat ? '메시지를 입력해주세요' : `${statusMeta.label} — 상담사가 대화를 시작하면 입력할 수 있어요`}
                rows={1}
                style={{ flex: 1, margin: 0 }}
              />
              <Button variant="primary" onClick={handleSend} disabled={!canChat}>전송</Button>
            </div>
          )}
        </div>
      )}
    </Page>
  );
}
